import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Col, Label, Row } from "reactstrap";
import { useAnalyticsCore } from "../../cores/analytics";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import { useYoutubeCore, isYoutubeConnected } from "../../cores/youtube";
import { YoutubeAnalyticsSnapshot } from "../component/analytics/youtubeAnalyticsMock";
import { YouTubeAnalyticsDashboard } from "../component/analytics/YouTubeAnalyticsDashboard";
import { TikTokAnalyticsDateRangeFilter } from "../component/analytics/TikTokAnalyticsDateRangeFilter";
import { PanelWembii } from "../component/PanelWembii";
import Content from "../layout/Content";
import Layout from "../layout/Layout";
import "../component/analytics/YouTubeAnalytics.scss";

const toIsoRange = (since: string, until: string): { since: string; until: string } => {
  const parse = (v: string) =>
    moment(v, ["MM/DD/YY", "MM/DD/YYYY", "YYYY-MM-DD"], true);
  const s = parse(since);
  const u = parse(until);
  if (!s.isValid() || !u.isValid()) {
    return { since, until };
  }
  return {
    since: s.format("YYYY-MM-DD"),
    until: u.format("YYYY-MM-DD"),
  };
};

export const YouTubeAnalytics = () => {
  const intl = useIntl();
  const { goToSocial } = useRouterCore();
  const { date } = useAnalyticsCore();
  const {
    company,
    companyPlan,
    companyRole,
    isAdmin,
    isReseller,
  } = useMeCore();
  const { settings, get, getInsights } = useYoutubeCore();

  const isSync = useMemo(() => isYoutubeConnected(settings), [settings]);

  const [snapshot, setSnapshot] = useState<YoutubeAnalyticsSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (company?.id) get(String(company.id));
  }, [company, get]);

  useEffect(() => {
    if (
      !isSync ||
      !companyPlan?.rules.allowAnalytics ||
      (!companyRole?.analitycs &&
        !companyRole?.owner &&
        !companyRole?.admin &&
        !companyRole?.agent &&
        !isAdmin &&
        !isReseller)
    )
      goToSocial();
  }, [goToSocial, isSync, companyPlan, companyRole, isAdmin, isReseller]);

  useEffect(() => {
    if (!isSync || !company?.id) return;
    const { since, until } = toIsoRange(date.since, date.until);
    setLoading(true);
    setLoadError(null);
    getInsights(String(company.id), since, until)
      .then((data) => setSnapshot(data))
      .catch((error) => {
        console.error(error);
        setSnapshot(null);
        setLoadError(
          error?.response?.data?.message ||
            intl.formatMessage({ id: "app.youtubeAnalytics.loadError" })
        );
      })
      .finally(() => setLoading(false));
  }, [company?.id, date.since, date.until, isSync, getInsights, intl]);

  return (
    <Layout>
      <Content>
        <div className="youtube-analytics-page-header tiktok-analytics-page-header tiktok-analytics-page-header--analytics">
          <div className="tiktok-analytics-page-header__toolbar">
            <div className="tiktok-analytics-page-header__lead">
              <h1 className="tiktok-analytics-page-header__title">
                {intl.formatMessage({ id: "app.youtubeAnalytics.title" })}
              </h1>
              <div
                className="youtube-analytics-page-header__logo tiktok-analytics-page-header__logo tiktok-analytics-page-header__logo--subtitle"
                aria-hidden
              >
                <FontAwesomeIcon icon={["fab", "youtube"]} />
              </div>
            </div>
            <div className="tiktok-analytics-page-header__right">
              <TikTokAnalyticsDateRangeFilter />
            </div>
          </div>
        </div>
        {loading ? (
          <Row className="d-flex justify-content-center mb-3">
            <Col xs="12" md="10" lg="8">
              <PanelWembii className="mb-2">
                <div className="d-flex justify-content-center py-4 text-muted">
                  {intl.formatMessage({ id: "app.reports.updating" })}
                </div>
              </PanelWembii>
            </Col>
          </Row>
        ) : null}
        {!loading && snapshot ? (
          <YouTubeAnalyticsDashboard snapshot={snapshot} />
        ) : null}
        {!loading && !snapshot && isSync ? (
          <Row className="d-flex justify-content-center mb-3">
            <Col xs="12" md="10" lg="8">
              <PanelWembii className="mb-2">
                <Label className="text-muted text-center mb-0 w-100 py-3">
                  {loadError ||
                    intl.formatMessage({ id: "app.youtubeAnalytics.noData" })}
                </Label>
              </PanelWembii>
            </Col>
          </Row>
        ) : null}
      </Content>
    </Layout>
  );
};
